import { useEffect, useState } from 'react';
import { callFn } from '../../lib/api';
import type { Location, ReservationTag } from '../../lib/types';
import { useApp } from '../../app/AppContext';
import { DataTable, type Column } from '../../components/DataTable';

export function Settings() {
  const { isSuperAdmin, reservationTags, reload, toast } = useApp();
  const [locations, setLocations] = useState<Location[]>([]);
  const [limit, setLimit] = useState(3);
  const [days, setDays] = useState(14);
  const [targets, setTargets] = useState('foster_and_admins');
  const [loading, setLoading] = useState(true);
  const [editLoc, setEditLoc] = useState<Location | 'new' | null>(null);
  const [editTag, setEditTag] = useState<{ id?: string; name: string; color: string; sort_order: number } | 'new' | null>(null);

  async function saveTag(t: { id?: string; name: string; color: string; sort_order: number }) {
    if (!t.name.trim()) { toast('Tag name required', 'err'); return; }
    try {
      await callFn('upsert_reservation_tag', { ...(t.id ? { id: t.id } : {}), name: t.name.trim(), color: t.color || null, sort_order: t.sort_order });
      toast('Saved', 'ok'); setEditTag(null); void reload();
    } catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }
  async function deleteTag(id: string) {
    if (!confirm('Delete this tag? It will be removed from any reservations it was applied to.')) return;
    try { await callFn('delete_reservation_tag', { id }); toast('Deleted', 'ok'); void reload(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }

  async function load() {
    setLoading(true);
    try {
      setLocations(await callFn<Location[]>('list_locations', { includeInactive: true }));
      const s = await callFn<{ monthly_reservation_limit: number; unpickup_notify_days: number; unpickup_notify_targets: string }>('get_settings');
      setLimit(s.monthly_reservation_limit); setDays(s.unpickup_notify_days); setTargets(s.unpickup_notify_targets);
    } catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  async function saveSettings() {
    try {
      await callFn('update_settings', { monthly_reservation_limit: limit, unpickup_notify_days: days, unpickup_notify_targets: targets });
      toast('Settings saved', 'ok'); void reload();
    } catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <h1 className="page-title">Settings</h1>
      <p className="page-sub">Pickup locations, the monthly limit, and overdue-pickup notifications.</p>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, color: 'var(--navy)' }}>Rules</h3>
        <div className="row">
          <label className="field">Monthly reservation limit (line-items)
            <input type="number" min={0} value={limit} onChange={(e) => setLimit(Number(e.target.value))} disabled={!isSuperAdmin} />
          </label>
          <label className="field">Notify if not picked up after (days)
            <input type="number" min={0} value={days} onChange={(e) => setDays(Number(e.target.value))} disabled={!isSuperAdmin} />
          </label>
          <label className="field">Overdue notice goes to
            <select value={targets} onChange={(e) => setTargets(e.target.value)} disabled={!isSuperAdmin}>
              <option value="foster_and_admins">Foster &amp; admins</option>
              <option value="foster">Foster only</option>
              <option value="admins">Admins only</option>
            </select>
          </label>
        </div>
        {isSuperAdmin ? <button className="btn" style={{ marginTop: 12 }} onClick={saveSettings}>Save rules</button>
          : <p className="page-sub" style={{ marginBottom: 0 }}>Only a super-admin can change these.</p>}
      </div>

      <div className="toolbar">
        <h3 style={{ margin: 0, color: 'var(--navy)' }}>Pickup locations</h3>
        <div className="spacer" />
        <button className="btn small" onClick={() => setEditLoc('new')}>+ Add location</button>
      </div>
      <DataTable
        columns={[
          { key: 'name', label: 'Name', get: (l: Location) => l.name },
          { key: 'address', label: 'Address', get: (l: Location) => l.address || '—' },
          { key: 'phone', label: 'Phone', get: (l: Location) => l.phone || '—' },
          { key: 'code', label: 'Code', get: (l: Location) => l.short_code || '—' },
          { key: 'active', label: 'Active', get: (l: Location) => (l.is_active ? 'yes' : 'no'), render: (l: Location) => <span className={`badge ${l.is_active ? 'new' : 'gray'}`}>{l.is_active ? 'yes' : 'no'}</span> },
        ] as Column<Location>[]}
        rows={locations}
        rowKey={(l) => l.id}
        exportName="locations"
        empty="No locations yet."
        actions={(l) => <button className="btn ghost small" onClick={() => setEditLoc(l)}>Edit</button>}
      />

      <div className="toolbar" style={{ marginTop: 26 }}>
        <h3 style={{ margin: 0, color: 'var(--navy)' }}>Item tags</h3>
        <div className="spacer" />
        <button className="btn small" onClick={() => setEditTag('new')}>+ Add tag</button>
      </div>
      <p className="page-sub" style={{ marginTop: 0 }}>Labels you can apply to catalog items (e.g. &ldquo;Wish list &ndash; Amazon&rdquo;, &ldquo;Kitten&rdquo;, &ldquo;Fragile&rdquo;). Define them here, then apply them to items on the Catalog page.</p>
      <DataTable
        columns={[
          { key: 'tag', label: 'Tag', get: (t: ReservationTag) => t.name, render: (t: ReservationTag) => <span className="tag-chip" style={{ background: t.color || '#dce6f1' }}>{t.name}</span> },
          { key: 'order', label: 'Order', get: (t: ReservationTag) => t.sort_order, numeric: true, align: 'right' },
        ] as Column<ReservationTag>[]}
        rows={reservationTags}
        rowKey={(t) => t.id}
        exportName="reservation-tags"
        empty="No tags yet."
        actions={(t) => (
          <>
            <button className="btn ghost small" onClick={() => setEditTag({ id: t.id, name: t.name, color: t.color || '#2e6da4', sort_order: t.sort_order })}>Edit</button>
            <button className="btn ghost small" onClick={() => deleteTag(t.id)}>Delete</button>
          </>
        )}
      />

      {editLoc && <LocModal loc={editLoc === 'new' ? null : editLoc} onClose={() => setEditLoc(null)} onDone={() => { setEditLoc(null); void load(); void reload(); }} />}
      {editTag && <TagModal tag={editTag === 'new' ? null : editTag} onClose={() => setEditTag(null)} onSave={saveTag} />}
    </>
  );

  function TagModal({ tag, onClose, onSave }: { tag: { id?: string; name: string; color: string; sort_order: number } | null; onClose: () => void; onSave: (t: { id?: string; name: string; color: string; sort_order: number }) => void }) {
    const [name, setName] = useState(tag?.name || '');
    const [color, setColor] = useState(tag?.color || '#2e6da4');
    const [sortOrder, setSortOrder] = useState(tag?.sort_order ?? 0);
    return (
      <div className="modal-bg" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>{tag ? 'Edit tag' : 'Add tag'}</h3>
          <div className="stack">
            <label className="field">Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="On hold" /></label>
            <div className="row">
              <label className="field">Color<input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ height: 40, padding: 4 }} /></label>
              <label className="field">Sort order<input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} /></label>
            </div>
            <div className="row">
              <button className="btn secondary" onClick={onClose}>Cancel</button>
              <button className="btn" onClick={() => onSave({ ...(tag?.id ? { id: tag.id } : {}), name, color, sort_order: sortOrder })}>Save</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function LocModal({ loc, onClose, onDone }: { loc: Location | null; onClose: () => void; onDone: () => void }) {
    const [name, setName] = useState(loc?.name || '');
    const [address, setAddress] = useState(loc?.address || '');
    const [phone, setPhone] = useState(loc?.phone || '');
    const [shortCode, setShortCode] = useState(loc?.short_code || '');
    const [isActive, setIsActive] = useState(loc?.is_active ?? true);
    const [sortOrder, setSortOrder] = useState(loc?.sort_order ?? 0);
    const [busy, setBusy] = useState(false);

    async function save() {
      if (!name.trim()) { toast('Name required', 'err'); return; }
      setBusy(true);
      try {
        await callFn('upsert_location', {
          ...(loc ? { id: loc.id } : {}),
          name: name.trim(), address: address.trim() || null, phone: phone.trim() || null,
          short_code: shortCode.trim() || null, is_active: isActive, sort_order: sortOrder,
        });
        toast('Saved', 'ok'); onDone();
      } catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); setBusy(false); }
    }

    return (
      <div className="modal-bg" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>{loc ? 'Edit location' : 'Add location'}</h3>
          <div className="stack">
            <label className="field">Name<input value={name} onChange={(e) => setName(e.target.value)} /></label>
            <label className="field">Address<input value={address} onChange={(e) => setAddress(e.target.value)} /></label>
            <div className="row">
              <label className="field">Phone<input value={phone} onChange={(e) => setPhone(e.target.value)} /></label>
              <label className="field">Short code<input value={shortCode} onChange={(e) => setShortCode(e.target.value)} placeholder="WWS" /></label>
            </div>
            <div className="row">
              <label className="field">Sort order<input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} /></label>
              <label className="field">Active
                <select value={isActive ? '1' : '0'} onChange={(e) => setIsActive(e.target.value === '1')}>
                  <option value="1">Yes</option><option value="0">No</option>
                </select>
              </label>
            </div>
            <div className="row">
              <button className="btn secondary" onClick={onClose}>Cancel</button>
              <button className="btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
